import * as React from "react";
import { Capacitor } from "@capacitor/core";

const MOBILE_BREAKPOINT = 768;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}

// Matches the iPad/large-screen breakpoint used by the layout CSS
// (`@media (min-width: 641px)` in index.css). Initialized synchronously so the
// first render is already correct — avoids a mobile→desktop flash on iPad.
const WIDE_BREAKPOINT = 641;

export function useIsWideScreen() {
  const query = `(min-width: ${WIDE_BREAKPOINT}px)`;
  const [isWide, setIsWide] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setIsWide(mql.matches);
    mql.addEventListener("change", onChange);
    onChange();
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return isWide;
}

// Desktop / large-screen browser breakpoint. At/above this width the app swaps
// the touch (mobile + iPad) layout for the desktop sidebar shell. Kept above the
// iPad portrait width (641) so tablets keep their existing touch experience.
// Initialized synchronously to avoid a touch→desktop flash on first paint.
const DESKTOP_BREAKPOINT = 1024;

// The desktop console is web-only by design — the native build always uses the
// touch layout, even on an iPad in landscape (which is >=1024px CSS px). So
// native platforms are never "desktop", regardless of width.
const isNative = Capacitor.isNativePlatform();

// Non-hook desktop check for use outside React (e.g. the router's view-transition
// config). Mirrors useIsDesktop: web-only, >=1024px.
export function isDesktopViewport() {
  if (isNative || typeof window === "undefined") return false;
  return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;
}

export function useIsDesktop() {
  const query = `(min-width: ${DESKTOP_BREAKPOINT}px)`;
  const [isDesktop, setIsDesktop] = React.useState(() =>
    !isNative && typeof window !== "undefined"
      ? window.matchMedia(query).matches
      : false,
  );

  React.useEffect(() => {
    if (isNative) return;
    const mql = window.matchMedia(query);
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    onChange();
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return isDesktop;
}
